import React from "react";

import { Typography, Tooltip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import InfoIcon from "@mui/icons-material/Info";
import { Call } from "@twilio/voice-sdk";
import clsx from "clsx";

import { edgeNameMap } from "../../../../constants";
import { Edge, NetworkTestName, TestResults } from "../../../../types";
import ProgressBar from "../../../ProgressBar/ProgressBar";
import ResultIcon from "../../../ResultWidget/ResultIcon/ResultIcon";
import { BITRATE_TEST_DURATION } from "../Tests/Tests";
import getTooltipContent from "./getTooltipContent";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    border: "1px solid #ddd",
    borderRadius: "3px",
    display: "flex",
    padding: "0.8em",
    background: "#eee",
    alignItems: "center",
    justifyContent: "space-between",
  },
  progressContainer: {
    flex: 1,
    padding: "0 1em",
  },
  edgeLabel: {
    fontSize: "14px",
    minWidth: "50px",
    width: "15%",
    whiteSpace: "nowrap",
  },
  iconContainer: {
    width: "15%",
    display: "flex",
    justifyContent: "flex-end",
    "& svg": {
      margin: "0 0.3em",
    },
  },
  pendingTest: {
    opacity: 0.5,
  },
}));

interface EdgeResultProps {
  codecPreferences: Call.Codec[];
  edge: Edge;
  isActive: boolean;
  result?: TestResults;
  activeTest?: NetworkTestName;
}

const progressBarTimings = {
  "Preflight Test": {
    position: 62.5,
    duration: 25,
  },
  "Bitrate Test": {
    position: 100,
    duration: BITRATE_TEST_DURATION / 1000,
  },
};

export default function EdgeResult(props: EdgeResultProps) {
  const { edge, isActive, result, activeTest } = props;
  const classes = useStyles();

  const progressDuration = activeTest ? progressBarTimings[activeTest].duration : 0;
  const progressPosition = activeTest ? progressBarTimings[activeTest].position : 0;

  return (
    <div className={clsx(classes.container, { [classes.pendingTest]: !isActive && !result })}>
      <Typography className={classes.edgeLabel}>{`${edgeNameMap[edge]}`}</Typography>
      <div className={classes.progressContainer}>
        {isActive && <ProgressBar position={progressPosition} duration={progressDuration} />}
      </div>
      {result && (
        <div className={classes.iconContainer}>
          <ResultIcon result={result} />
          <Tooltip title={getTooltipContent(result)} placement="top" disableInteractive>
            <InfoIcon />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
